import { alpha } from "@mui/material/styles";
import {
  contentCard,
  formField,
  inkfree,
  notification,
  signer,
  statusChip,
  white,
} from "theme/colors";

/**
 * "Build with AI" (Figma "Section 2"): the build-choice dialog and the build
 * screen. Every colour is an existing palette token; the mock's own hex values
 * sit within a few points of these and were mapped onto them (see the
 * design-styling preference: the app's colours and AppModal chrome win).
 */
const aiWebFormTokens = {
  color: {
    ink: signer.ink,
    body: signer.text.secondary,
    sub: signer.text.meta,
    faint: signer.text.faint,
    placeholder: formField.placeholderMuted,
    brand: inkfree.main,
    brandTint: signer.orange.tint,
    brandFaint: signer.orange.faint,
    brandBorder: signer.orange.border,
    surface: white,
    canvas: signer.surface.canvas,
    subtle: signer.surface.subtle,
    chipBg: formField.disabledBg,
    colleagueBubble: signer.surface.pill,
    border: formField.border,
    divider: contentCard.border,
    hairline: signer.border.divider,
    lightBorder: signer.border.light,
    strongBorder: signer.border.strong,
    fieldBg: formField.bg,
    // Green "what changed" chips and the Current badge.
    successBg: notification.success.bg,
    successText: notification.success.title,
    // Amber notices: contrast, label warnings, a design changed meanwhile.
    warnBg: notification.warning.bg,
    warnBorder: notification.warning.border,
    warnText: notification.warning.text,
    warnTitle: notification.warning.title,
    // Grey notices: a kept turn, the restore note.
    neutralBg: notification.info.bg,
    neutralBorder: notification.info.border,
    neutralText: notification.info.title,
    neutralIcon: notification.info.icon,
    // Blue: the Published badge and the "this replaces" notice.
    infoBg: statusChip.tone.info.bg,
    infoText: statusChip.tone.info.text,
    infoBorder: alpha(statusChip.tone.info.text, 0.18),
    // The preview banner and the phone mock's frame.
    dark: signer.ink,
    darkOverlay: alpha(white, 0.12),
    headerShadow: alpha(inkfree.main, 0.25),
    composerRing: alpha(inkfree.main, 0.12),
    segmentShadow: alpha(signer.ink, 0.12),
    panelShadow: alpha(signer.ink, 0.1),
    bannerShadow: alpha(signer.ink, 0.25),
  },
  layout: {
    headerHeight: 72,
    headerPadX: 28,
    phoneHeaderPadX: 12,
    tabBarHeight: 57,
    /** Chat column per width: 1440+ as designed, then narrower so the preview keeps room. */
    chatWidthXl: 540,
    chatWidthLg: 480,
    chatWidthMd: 400,
    chatPadX: 24,
    phoneChatPadX: 16,
    versionsWidth: 440,
    /**
     * The narrowest window where the previewing banner still fits beside the open
     * versions panel in two lines: chat 540 + panel 440 + banner margins 72 + about
     * 480 for its text and two buttons. Narrower, the panel's own row carries Restore.
     */
    bannerBesidePanelMinWidth: 1536,
    previewHeaderHeight: 60,
    canvasPad: 28,
    phoneCanvasPad: 16,
    desktopMaxWidth: 1180,
    phoneWidth: 375,
    phoneBezel: 10,
    phoneRadius: 44,
    phoneScreenRadius: 34,
    phoneStatusHeight: 34,
    phoneMaxHeight: 800,
    userBubbleMax: 380,
    replyMax: 470,
dialogWidth: 600,
      publishWidth: 640,
      // selection prompt
      selectionPromptZIndex: 1300,
      selectionPromptMaxWidth: 360,
      selectionPromptBorderRadius: "8px",
      selectionPromptMobileBottom: 16,
    },
    radius: {
    card: "12px",
    bubble: "14px",
    bubbleTail: "4px",
    control: "9px",
    iconButton: "10px",
    tile: "11px",
    smallTile: "8px",
    notice: "10px",
    composer: "14px",
    pill: "999px",
    versionChip: "6px",
  },
  font: {
    title: { fontSize: "16px", fontWeight: 800 },
    panelTitle: { fontSize: "15px", fontWeight: 800 },
    dialogTitle: { fontSize: "17px", fontWeight: 800 },
    body: { fontSize: "14px", fontWeight: 500, lineHeight: "21px" },
    control: { fontSize: "13px", fontWeight: 700 },
    small: { fontSize: "12px", fontWeight: 500 },
    smallStrong: { fontSize: "12px", fontWeight: 600 },
    smallBold: { fontSize: "12px", fontWeight: 700 },
    meta: { fontSize: "11px", fontWeight: 600 },
    eyebrow: { fontSize: "11px", fontWeight: 700, letterSpacing: "0.6px", textTransform: "uppercase" as const },
    badge: { fontSize: "10px", fontWeight: 700, letterSpacing: "0.6px", textTransform: "uppercase" as const },
  },
};

export default aiWebFormTokens;
